import { GlobeIcon, ListTreeIcon, ScaleIcon, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { formatCode } from "../../../shared/uktzed.ts";
import { Logo } from "@/components/brand/logo.tsx";
import { LanguageSwitch } from "@/components/common/language-switch.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { isMockTransport } from "@/lib/http.ts";
import { useAuthConfig, useDocumentTitle, useMe } from "@/lib/queries.ts";
import { FullPageLoader } from "./guards.tsx";

const VALUE_ICONS: LucideIcon[] = [GlobeIcon, ListTreeIcon, ScaleIcon];

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden>
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.57-5.17 3.57-8.81Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.88-3.01c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.95H1.27v3.11A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.9 12c0-.8.14-1.57.37-2.29V6.6H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.4l4-3.11Z" />
      <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.27 6.6l4 3.11C6.22 6.88 8.87 4.77 12 4.77Z" />
    </svg>
  );
}

/** A fragment of the customs declaration with box 33 stamped, behind the brand text. */
function DeclarationFragment() {
  const { t } = useI18n();
  const form = t.brand.form;
  const cell = "flex flex-col gap-1 border-white/15 px-3 py-2";
  const label = "flex gap-1.5 text-[10px] leading-tight text-white/45";
  const value = "code-mono text-[12px] text-white/80";
  return (
    <div aria-hidden className="relative rounded-lg border border-white/15 bg-white/[0.03] text-white shadow-2xl shadow-black/30 backdrop-blur-sm">
      <div className="grid grid-cols-[1.35fr_0.5fr_1.25fr]">
        <div className={`${cell} row-span-2 border-r`}>
          <span className={label}>
            <b className="font-semibold text-white/70">31</b>
            {form.box31}
          </span>
          <span className="text-[12px] leading-snug text-white/75">{form.box31Value}</span>
        </div>
        <div className={`${cell} border-r border-b`}>
          <span className={label}>
            <b className="font-semibold text-white/70">32</b>
            {form.box32}
          </span>
          <span className={value}>1</span>
        </div>
        <div className={`${cell} relative border-b`}>
          <span className="pointer-events-none absolute -inset-px rounded-[3px] border-2 border-stamp-bright" />
          <span className={label}>
            <b className="font-semibold text-stamp-bright">33</b>
            <span className="text-white/70">{form.box33}</span>
          </span>
          <span className="code-mono text-[14px] font-semibold tracking-[0.02em] text-white">{formatCode("8482109000")}</span>
        </div>
        <div className={`${cell} border-r`}>
          <span className={label}>
            <b className="font-semibold text-white/70">34</b>
            {form.box34}
          </span>
          <span className={value}>SE</span>
        </div>
        <div className={cell}>
          <span className={label}>
            <b className="font-semibold text-white/70">35</b>
            {form.box35}
          </span>
          <span className={value}>2,64</span>
        </div>
      </div>
    </div>
  );
}

function BrandPanel() {
  const { t } = useI18n();
  return (
    <section className="relative isolate overflow-hidden bg-ink px-6 py-8 text-white sm:px-10 lg:flex lg:min-h-dvh lg:flex-col lg:justify-between lg:px-14 lg:py-12">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.07] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_30%_20%,black,transparent_75%)]"
      />
      <div className="flex items-center gap-3">
        <Logo size={44} accent className="text-white" title={t.brand.name} />
        <span className="text-xl font-bold tracking-[-0.02em]">{t.brand.name}</span>
      </div>

      <div className="mt-8 max-w-xl lg:mt-0">
        <h1 className="text-[26px] leading-[1.2] font-bold tracking-[-0.025em] text-balance sm:text-[32px] lg:text-[38px]">{t.brand.tagline}</h1>
        <ul className="mt-7 flex flex-col gap-4 lg:mt-9">
          {t.brand.values.map((item, index) => {
            const Icon = VALUE_ICONS[index] ?? GlobeIcon;
            return (
              <li key={item.title} className="flex gap-3.5">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/[0.06]">
                  <Icon className="size-[18px] text-white/85" aria-hidden />
                </span>
                <div>
                  <p className="text-[14.5px] font-semibold text-white">{item.title}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-white/60">{item.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-10 hidden max-w-xl lg:block">
        <DeclarationFragment />
        <p className="mt-3 text-[11.5px] text-white/40">{t.brand.formCaption}</p>
      </div>
    </section>
  );
}

/**
 * `/auth/*` are server routes, so they are opened as full page loads. With fixture data there is no
 * server, and the page's own `/auth/*` route completes the fake sign-in instead.
 */
function useAuthNavigation() {
  const navigate = useNavigate();
  return (path: string) => {
    if (isMockTransport()) void navigate(path);
    else window.location.assign(path);
  };
}

function DevSignIn() {
  const { t, language } = useI18n();
  const go = useAuthNavigation();
  const [email, setEmail] = useState("dev@box33.local");
  return (
    <form
      className="flex flex-col gap-2.5 rounded-lg border border-dashed bg-muted/40 p-3.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (email.trim()) go(`/auth/dev?email=${encodeURIComponent(email.trim())}&language=${language}`);
      }}
    >
      <div>
        <p className="text-[12.5px] font-semibold">{t.auth.devTitle}</p>
        <p className="text-[11.5px] text-muted-foreground">{t.auth.devHint}</p>
      </div>
      <Label htmlFor="dev-email" className="sr-only">
        {t.auth.devEmail}
      </Label>
      <div className="flex gap-2">
        <Input id="dev-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="h-8 text-[13px]" />
        <Button type="submit" size="sm" variant="secondary">
          {t.auth.devSubmit}
        </Button>
      </div>
    </form>
  );
}

/** Sign-in: brand panel on ink navy, and a card with Continue with Google. */
export function LoginPage() {
  const { t, language } = useI18n();
  const me = useMe();
  const config = useAuthConfig();
  const go = useAuthNavigation();
  useDocumentTitle(t.auth.title, t.brand.name);

  if (me.isPending) return <FullPageLoader />;
  if (me.data) return <Navigate to={me.data.tenant.status === "active" ? "/app" : "/waitlist"} replace />;

  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <BrandPanel />
      <section className="flex flex-col px-4 py-6 sm:px-8">
        <div className="flex justify-end">
          <LanguageSwitch />
        </div>
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[400px] rounded-2xl border bg-card p-6 shadow-[0_10px_40px_-12px_rgba(15,27,45,0.18)] sm:p-8">
            <h2 className="text-[22px] font-bold tracking-[-0.02em]">{t.auth.welcome}</h2>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">{t.auth.subtitle}</p>
            <Button asChild size="lg" variant="outline" className="mt-6 w-full gap-3 text-[14.5px]">
              <a
                href={`/auth/google?language=${language}`}
                onClick={(event) => {
                  if (!isMockTransport()) return;
                  event.preventDefault();
                  go("/auth/google");
                }}
              >
                <GoogleMark />
                {t.auth.google}
              </a>
            </Button>
            <p className="mt-3 text-center text-[12px] text-muted-foreground">{t.auth.approvalNote}</p>
            {config.data?.devLogin ? (
              <>
                <Separator className="my-5" />
                <DevSignIn />
              </>
            ) : null}
            <p className="mt-6 text-center text-[11.5px] leading-relaxed text-muted-foreground">
              {t.auth.agreePrefix}{" "}
              <Link to="/terms" className="font-semibold text-foreground underline-offset-2 hover:underline">
                {t.auth.termsLink}
              </Link>{" "}
              {t.auth.agreeAnd}{" "}
              <Link to="/privacy" className="font-semibold text-foreground underline-offset-2 hover:underline">
                {t.auth.privacyLink}
              </Link>
              {t.auth.agreeSuffix}
            </p>
          </div>
        </div>
        <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">
            {t.common.privacy}
          </Link>
          <Link to="/terms" className="hover:text-foreground">
            {t.common.terms}
          </Link>
          <span>{t.auth.copyright}</span>
        </footer>
      </section>
    </div>
  );
}
